import React from 'react';

import {
    ConfigurationProvider,
    useCurationConfigValue,
    useGetFocusedInstanceIdValue
} from '../../../hooks/widgetConfiguration.js';

import {
    useCompany,
    useLandscape
} from '../../../hooks/state.js';

import {
    ChannelAuthWidget
} from '../templates/widgets/ChannelAuthWidget.jsx';

import {
    useIsExportingPDF
} from '../../../hooks/useExport.js';
import {
    useSetExportConfig
} from '../../../hooks/exportConfigurationHooks.js';

import {
    useUpdateShellPrimary,
    useUpdateShellSecondary
} from '../../shell/hooks/shellHooks.js';

import useGetPrivateMetadata from '../hooks/useGetPrivateMetadata.js';
import RIQLoading from '../../../components/RIQLoading.jsx';

import {
    ExportHeaders
} from '../../../components/ExportHeaders.jsx';

import usePrivateDataTitle from '../../../hooks/usePrivateDataTitle.js';
import useIsPrivateDataLoading from '../../../hooks/useIsPrivateDataLoading.js';

import ActivityAndVideoViews from '../components/ActivityAndVideoViews.jsx';
import DimensionAnalysis from '../components/DimensionAnalysis.jsx';
import Posts from '../components/Posts.jsx';
import PrivateDataLoading from '../components/PrivateDataLoading.jsx';
import Toolbar from '../components/Toolbar.jsx';

const widgets = [
    {
        widgets: [
            {
                instanceId: 'activity-and-video-views',
                component: ActivityAndVideoViews
            },
            {
                instanceId: 'hashtag-analysis',
                component: DimensionAnalysis,
                props: {
                    dimension: 'hashtag'
                }
            }
        ]
    },
    {
        instanceId: 'posts',
        component: Posts
    },
    {
        widgets: [
            {
                instanceId: 'postTag-analysis',
                component: DimensionAnalysis,
                props: {
                    dimension: 'postTagId'
                }
            },
            {
                instanceId: 'keyword-analysis',
                component: DimensionAnalysis,
                props: {
                    dimension: 'keyword'
                }
            }
        ]
    }
];

function Widgets(appSpecificExportConfig) {
    const onlyInstanceId = useCurationConfigValue('onlyInstanceId');
    const showOnlyInstanceId = useGetFocusedInstanceIdValue() || onlyInstanceId;
    const isPDFExport = useIsExportingPDF();

    let filtered = widgets;
    if (showOnlyInstanceId) {
        filtered = widgets.reduce(function widgetReducer(memo, widget) {
            if (widget.widgets) {
                return widget.widgets.reduce(widgetReducer, memo);
            }
            return (showOnlyInstanceId === widget.instanceId) ? memo.concat(widget) : memo;
        }, []);
    }

    return filtered.reduce(function widgetRenderer(memo, widget, idx) {
        if (widget.widgets) {
            return memo.concat(
                <section
                    key={`widgets-${idx}`}
                    className='tlw-grid tlw-grid-cols-1 md_tlw-grid-cols-2 md_tlw-gap-10'
                >
                    {widget.widgets.reduce(widgetRenderer, [])}
                </section>
            );
        }

        const props = _.defaults({
            key: widget.instanceId,
            instanceId: widget.instanceId,
            appSpecificExportConfig: appSpecificExportConfig.appSpecificExportConfig
        }, widget.props);

        const element = React.createElement(widget.component, props);
        return memo.concat(
            (isPDFExport ?
                <section className='tlw-grid tlw-break-before-page tlw-grid-cols-1 md_tlw-grid-cols-2'>
                    {element}
                </section> :
                element)
        );
    }, []);
}

function getCurationUrlRenderer(instanceId) {
    return (companyId) => {
        const baseUrl = `insights/tiktok/${companyId}/posts`;
        if (!instanceId) {
            return baseUrl;
        }
        return `${baseUrl}/details/${instanceId}`;
    };
}

function CurationLayout({privateMetadata}) {

    const companyId = useCurationConfigValue('companyId');
    const landscapeId = useCurationConfigValue('landscapeId');

    const onlyInstanceId = useCurationConfigValue('onlyInstanceId');
    const showOnlyInstanceId = useGetFocusedInstanceIdValue() || onlyInstanceId;

    const landscapeName = useLandscape(landscapeId)?.name;
    const companyName = useCompany(companyId)?.name;
    const timePeriod = useCurationConfigValue(null, 'timePeriod');

    const sourceDetail = RivalIQ.enums.sourceDetails[RivalIQ.enums.sources.TIKTOK_INSIGHTS];

    const isPrivateDataLoading = useIsPrivateDataLoading(companyId, sourceDetail);

    const navigationTitle = usePrivateDataTitle('TikTok Insights', companyId, privateMetadata.companies, getCurationUrlRenderer(showOnlyInstanceId));
    useUpdateShellPrimary(navigationTitle, {
        companySets: {
            disabled: true
        }
    });

    const authWidget = ChannelAuthWidget(privateMetadata, RivalIQ.enums.sourceDetails[RivalIQ.enums.sources.TIKTOK_INSIGHTS]);

    useUpdateShellSecondary(!authWidget && !isPrivateDataLoading, {
        page: RivalIQ.configuration.menus.getConfiguration().TIKTOK_INSIGHTS
    });


    if (authWidget) {
        return authWidget;
    }

    if (!_.isFinite(companyId)) {
        const defaultCompanyId = _.first(privateMetadata.companies).id;
        const url = (showOnlyInstanceId ? `insights/tiktok/${defaultCompanyId}/posts/details/${showOnlyInstanceId}` : `insights/tiktok/${defaultCompanyId}/posts`);
        RivalIQ.execute('history:navigate', url, {
            trigger: true
        });
        return;
    }

    if (isPrivateDataLoading) {
        return <PrivateDataLoading
            sourceName={sourceDetail.name}
            loadingImageUrl={sourceDetail.privateDataLoadingImage}
        />;
    }

    const timePeriodStr = RivalIQ.utils.timePeriod.getTimePeriodDisplayString(timePeriod);

    const appSpecificExportConfig = {
        landscapeName: landscapeName,
        focusCompanyTitle: 'Focus Company',
        focusCompany: companyName,
        timePeriodStr: timePeriodStr
    };

    SetUpExports(timePeriodStr, appSpecificExportConfig);

    return (
        <>
            <ExportHeaders
                title={`TikTok Video Insights`}
                subtitles={[
                    `Landscape: ${landscapeName}`,
                    `Focus Company: ${companyName}`
                ]}
                timePeriodStr={timePeriodStr}
            />
            <Toolbar />
            <Widgets
                appSpecificExportConfig={appSpecificExportConfig}
            />
        </>
    );
}

function SetUpExports(timePeriodStr, appSpecificExportConfig) {
    useSetExportConfig({
        title: 'TikTok Video Insights',
        timePeriodStr: timePeriodStr,
        supportedExportFormats: [
            RivalIQ.enums.exportFormat.PNG,
            RivalIQ.enums.exportFormat.PDF,
            RivalIQ.enums.exportFormat.PPT
        ],
        supportedExportTypes: _.values(RivalIQ.enums.exportType),
        pptExportConfig: {
            showIntroSlide: true,
            slideConfigs: _.reduce(widgets, function slideReducer(memo, widget) {
                if (widget.widgets) {
                    return widget.widgets.reduce(slideReducer, memo);
                }
                const instanceId = widget.instanceId;
                return (instanceId ? memo.concat({instanceId}) : memo); // eslint-disable-line object-curly-newline
            }, [])
        },
        appSpecificExportConfig
    });
}

export default function TikTokInsights(props) {
    const defaultProps = _.extend({
        instanceId: props?.instanceId,
        wProps: {
            'posts': {
                sortMetricName: 'engagement_total',
                sortOrder: 'desc'
            }
        }
    }, props);

    const configurationSchema = {
        curationPropsConfig: {
            companyId: {
                urlPersistence: true
            }
        },
        widgetPropsConfig: {
            'posts': {
                sortMetricName: {
                    urlPersistence: true
                },
                sortOrder: {
                    urlPersistence: true
                }
            }
        }
    };

    const {data: privateMetadata, isLoading} = useGetPrivateMetadata(RivalIQ.enums.sourceDetails[RivalIQ.enums.sources.TIKTOK_INSIGHTS].channel);

    return (
        <>
            {isLoading && <RIQLoading />}

            {!isLoading &&
                <ConfigurationProvider
                    defaultProps={defaultProps}
                    configurationSchema={configurationSchema}
                >
                    <CurationLayout
                        privateMetadata={privateMetadata}
                    />
                </ConfigurationProvider>
            }
        </>
    );
}
